import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import * as ContentList from '@/components/ContentList';
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import { ModalVariant } from '@/components/Modals/types';
import { useTaskRefetch } from '@/modules/corporate/modules/tasks/context/TaskRefetchContext';
import { OkrTaskCreationConfig } from '@/types/schema';

import useSyncFromJiraModal from '../../hooks/useSyncFromJiraModal';
import { headerIconStyles } from '../AddTaskFromJiraModal/styles';
import AddJQLQueryDialog from './AddJQLQueryDialog';
import EmptyState from './EmptyState';
import ListItem from './ListItem';
import { AutoSyncFromJiraModalProps } from './types';

function AutoSyncFromJiraModal(props: AutoSyncFromJiraModalProps) {
  const { t } = useTranslation('Jira');
  const { provider, onClose, onSubmit } = props;
  const { state, methods } = useSyncFromJiraModal(props);
  const { taskCreationConfigs, inViewJQLQueryDialog, fetching } = state;
  const {
    closeJQLQueryDialog,
    onAddJQLQueryClick,
    setInViewJQLQueryDialog,
    onAddJQLQueryModalSubmit,
    onDeleteJQLQuery,
  } = methods;

  const { refetch } = useTaskRefetch();
  const _onCloseAfterSubmit = useCallback(() => {
    refetch?.();
    onSubmit();
  }, [onSubmit, refetch]);

  const renderContent = useCallback(() => {
    if (fetching) {
      return <Loader />;
    }

    if (!taskCreationConfigs?.length) {
      return <EmptyState provider={provider} onAddQuery={onAddJQLQueryClick} />;
    }

    if (taskCreationConfigs?.length) {
      return (
        <>
          <Stack className="mb-20 px-32">
            <ContentList.AddBlock title={t('Jira:AddJQLQuery')} onClick={onAddJQLQueryClick} />
          </Stack>
          <ContentListModal.Body
            className="flex-1"
            itemRenderer={(index: number) => {
              const taskCreationConfig = taskCreationConfigs[index] as OkrTaskCreationConfig;

              return (
                <ListItem
                  key={taskCreationConfig.id}
                  isFirstItem={index === 0}
                  provider={provider}
                  setInViewJQLQueryDialog={setInViewJQLQueryDialog}
                  taskCreationConfig={taskCreationConfig}
                  onDeleteJQLQuery={onDeleteJQLQuery}
                />
              );
            }}
          />
        </>
      );
    }

    return null;
  }, [
    fetching,
    onAddJQLQueryClick,
    onDeleteJQLQuery,
    provider,
    setInViewJQLQueryDialog,
    t,
    taskCreationConfigs,
  ]);

  return (
    <>
      <ContentListModal.Main
        action={{
          text: t('Common:Actions.Done'),
          onSubmit: _onCloseAfterSubmit,
          TertiaryAction: (
            <Typography color="text-text-placeholder" variant="label-02">
              {t('Jira:SyncsSet', { count: taskCreationConfigs?.length })}
            </Typography>
          ),
          isSubmitting: false,
        }}
        header={{
          title: t('Jira:Tasks.AutoSyncFromProvider', { providerName: provider.name }),
          description: t('Jira:Tasks.AutoSyncFromProviderDescription', {
            providerName: provider.name,
          }),
          avatar: {
            src: provider.logo.url,
            className: headerIconStyles,
          },
        }}
        items={taskCreationConfigs}
        showCloseBtn={false}
        variant={ModalVariant.ExtraWide}
        onClose={onClose}>
        {renderContent()}
      </ContentListModal.Main>
      {inViewJQLQueryDialog ? (
        <AddJQLQueryDialog
          inViewJQLQueryDialog={inViewJQLQueryDialog}
          provider={provider}
          onAddJQLQueryModalSubmit={onAddJQLQueryModalSubmit}
          onClose={closeJQLQueryDialog}
        />
      ) : null}
    </>
  );
}

export default AutoSyncFromJiraModal;
